import { buildQueryParams } from './queryParamsUtil';
import type { RequestErrorResponse } from './request';
import { request, withErrorHandler } from './request';

export class QueryRequest<
  TypeRequest,
  TypeBody extends string | object | undefined = undefined,
  TypeParams = unknown
> {
  private baseUrl: string;
  private headers: Headers;
  private tags?: string[];
  private timeoutMs?: number;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.headers = new Headers();
  }

  private async sendRequest(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body?: TypeBody,
    params?: TypeParams
  ): Promise<TypeRequest | RequestErrorResponse> {
    const queryParams = params ? `?${buildQueryParams(params)}` : '';
    const url = `${this.baseUrl}/${endpoint}${queryParams}`;
    const requestParams = {
      method: method,
      headers: this.headers,
      body: body && method !== 'GET' ? body : undefined,
      signal: this.timeoutMs ? AbortSignal.timeout(this.timeoutMs) : undefined,
    };
    return withErrorHandler(async () =>
      request<TypeRequest>(url, requestParams, this.tags)
    );
  }

  public addHeader(key: string, value: string): void {
    this.headers.set(key, value);
  }

  public addDefaultHeaders(): void {
    this.addHeader('Content-Type', 'application/json');
    this.addHeader('Accept', 'application/json, text/plain, */*');
  }

  public addTags(tags: string[]): void {
    this.tags = tags;
  }

  public addTimeout(ms: number): void {
    this.timeoutMs = ms;
  }

  public get(
    endpoint: string,
    params?: TypeParams
  ): Promise<TypeRequest | RequestErrorResponse> {
    return this.sendRequest(endpoint, 'GET', undefined, params);
  }

  public post(
    endpoint: string,
    body: TypeBody
  ): Promise<TypeRequest | RequestErrorResponse> {
    return this.sendRequest(endpoint, 'POST', body);
  }

  public put(
    endpoint: string,
    body: TypeBody
  ): Promise<TypeRequest | RequestErrorResponse> {
    return this.sendRequest(endpoint, 'PUT', body);
  }

  public patch(
    endpoint: string,
    body?: TypeBody
  ): Promise<TypeRequest | RequestErrorResponse> {
    return this.sendRequest(endpoint, 'PATCH', body);
  }

  public delete(endpoint: string): Promise<TypeRequest | RequestErrorResponse> {
    return this.sendRequest(endpoint, 'DELETE');
  }
}
